import { useApolloClient } from '@apollo/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useCreateMasterclassRatingsMutation,
  useListMasterclassRatingsQuery,
} from '@/@generated/codegen/API';

export const MASTERCLASS_RATINGS_QUERY_KEYS = {
  byCertificationProgress: (certificationProgressId: string, masterclassId: string) =>
    [
      'masterclass_ratings',
      'certification_progress',
      certificationProgressId,
      'masterclass',
      masterclassId,
    ] as const,
  byUserAndCase: (userId: string, caseId: string, masterclassId: string) =>
    ['masterclass_ratings', 'user', userId, 'case', caseId, 'masterclass', masterclassId] as const,
  byCase: (caseId: string, masterclassId: string) =>
    ['masterclass_ratings', 'case', caseId, 'masterclass', masterclassId] as const,
};

export const useMasterclassRatingByCertificationProgress = (
  certificationProgressId: string | null,
  masterclassId: string | null
) => {
  const { data, loading, error, refetch } = useListMasterclassRatingsQuery({
    variables: {
      where: {
        certificationProgressId: { equals: certificationProgressId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
      paginationArgs: { first: 1 },
    },
    skip: !certificationProgressId || !masterclassId,
  });

  const ratingData = data?.listMasterclassRatings?.edges[0]
    ? {
        id: data.listMasterclassRatings.edges[0].node.id,
      }
    : null;

  return {
    data: ratingData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useMasterclassRatingByUserAndCase = (
  userId: string | null,
  caseId: string | null,
  masterclassId: string | null
) => {
  const { data, loading, error, refetch } = useListMasterclassRatingsQuery({
    variables: {
      where: {
        userId: { equals: userId || '' },
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
      paginationArgs: { first: 1 },
    },
    skip: !userId || !caseId || !masterclassId,
  });

  const ratingData = data?.listMasterclassRatings?.edges[0]
    ? {
        id: data.listMasterclassRatings.edges[0].node.id,
      }
    : null;

  return {
    data: ratingData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useMasterclassRatings = (caseId: string | null, masterclassId: string | null) => {
  const { data, loading, error, refetch } = useListMasterclassRatingsQuery({
    variables: {
      where: {
        caseId: { equals: caseId || '' },
        masterclassId: { equals: masterclassId || '' },
      },
    },
    skip: !caseId || !masterclassId,
  });

  const ratingsData = data?.listMasterclassRatings?.edges.map((edge) => ({
    id: edge.node.id,
    userId: edge.node.userId,
    caseId: edge.node.caseId,
    certificationProgressId: edge.node.certificationProgressId,
    applicationRating: edge.node.applicationRating,
    masterclassRating: edge.node.masterclassRating,
    createdAt: edge.node.createdAt,
  }));

  return {
    data: ratingsData,
    isLoading: loading,
    error: error,
    refetch,
  };
};

export const useInsertMasterclassRating = () => {
  const queryClient = useQueryClient();
  const apolloClient = useApolloClient();
  const [createMutation] = useCreateMasterclassRatingsMutation();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      masterclassId,
      certificationProgressId,
      applicationRating,
      masterclassRating,
    }: {
      userId: string;
      caseId: string;
      masterclassId: string;
      certificationProgressId: string;
      applicationRating: number;
      masterclassRating: number;
    }) => {
      const result = await createMutation({
        variables: {
          data: {
            // Note: userId is not part of DTO - derived from auth context by backend
            caseId,
            masterclassId,
            certificationProgressId,
            applicationRating,
            masterclassRating,
          },
        },
      });

      if (result.errors) throw new Error(result.errors[0].message);
      if (!result.data?.createMasterclassRatings) throw new Error('Failed to create rating');

      return {
        id: result.data.createMasterclassRatings.id,
        userId: result.data.createMasterclassRatings.userId,
        caseId: result.data.createMasterclassRatings.caseId,
        masterclassId: result.data.createMasterclassRatings.masterclassId,
        certificationProgressId: result.data.createMasterclassRatings.certificationProgressId,
        applicationRating: result.data.createMasterclassRatings.applicationRating,
        masterclassRating: result.data.createMasterclassRatings.masterclassRating,
        createdAt: result.data.createMasterclassRatings.createdAt,
      };
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byCertificationProgress(
          data.certificationProgressId,
          data.masterclassId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byUserAndCase(
          data.userId,
          data.caseId,
          data.masterclassId
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byCase(data.caseId, data.masterclassId),
      });
      void apolloClient.refetchQueries({
        include: ['listMasterclassRatings'],
      });
    },
  });
};
